import { Listbox, Transition } from "@headlessui/react";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  BookOpenCheck,
  Check,
  CheckCircle2,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Mail,
  Phone,
  Search,
  UserRound,
  UsersRound,
} from "lucide-react";
import { Fragment, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { AssignCourseToStudent } from "../../api/Courses/AssignCourse";
import { GetAllCoursesList } from "../../api/Courses/GetAllCoursesList";
import { getStudents } from "../../api/Students/GetAllStudents";
import Button from "../../components/Shared/Button";
import Input from "../../components/Shared/Input";
import Spinner from "../../components/Shared/Spinner";
import { ButtonTypes } from "../../utils/consts";

type StudentsDTO = {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  status: string;
};

type CourseOption = {
  id: string;
  name: string;
  slug?: string;
};

const getStudentName = (student: StudentsDTO) =>
  `${student.firstName} ${student.lastName}`.trim();

const getInitials = (student: StudentsDTO) => {
  const first = student.firstName?.charAt(0) || "";
  const last = student.lastName?.charAt(0) || "";
  return `${first}${last}`.toUpperCase() || "NW";
};

const AssignCourse = () => {
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [firstName, setFirstName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [studentSelected, setStudentSelected] = useState<StudentsDTO | null>(
    null
  );
  const [selected, setSelected] = useState<CourseOption | null>(null);
  const [searchParams, setSearchParams] = useState({
    firstName: "",
    phone: "",
    email: "",
  });

  const handleSearch = () => {
    setSearchParams({ firstName, phone, email });
    setPage(1);
  };

  const { data: StudentsResponse, isLoading: isStudentsLoading } = useQuery({
    queryFn: () =>
      getStudents({
        page,
        limit,
        firstName: searchParams.firstName,
        status: "",
        phone: searchParams.phone,
        email: searchParams.email,
      }),
    queryKey: [
      "students",
      page,
      limit,
      searchParams.firstName,
      searchParams.phone,
      searchParams.email,
    ],
    staleTime: 0,
    refetchInterval: 0,
  });

  const { data: CoursesResponse, isLoading: isCourseLoading } = useQuery({
    queryFn: GetAllCoursesList,
    queryKey: ["coursesList"],
    staleTime: 0,
    refetchInterval: 0,
  });

  const courses = CoursesResponse?.data?.coursesData || [];
  const students = StudentsResponse?.data?.data || [];
  const metadata = StudentsResponse?.data?.metaData;

  useEffect(() => {
    if (
      courses.length &&
      (!selected || !courses.some((course) => course.id === selected.id))
    ) {
      setSelected(courses[0]);
    }
  }, [courses, selected]);

  const { mutate, isPending } = useMutation({
    mutationFn: AssignCourseToStudent,
    onSuccess: () => {
      toast.success("Student Added To Course Successfully");
      setSelected(courses[0] ?? null);
    },
    onError: () => {
      toast.error("Student already assigned to course");
    },
  });

  const handleAssignCourse = () => {
    if (selected && studentSelected) {
      mutate({
        courseId: selected.id,
        studentId: studentSelected.id,
      });
    } else {
      toast.error("Please select both a course and a student.");
    }
  };

  const handleNextPage = () => {
    if (metadata?.nextPage) {
      setPage(metadata.nextPage);
    }
  };

  const handlePrevPage = () => {
    if (metadata?.previousPage) {
      setPage(metadata.previousPage);
    }
  };

  const activeFilters = [
    searchParams.firstName,
    searchParams.phone,
    searchParams.email,
  ].filter(Boolean).length;
  const isLoading = isCourseLoading || isStudentsLoading;

  return (
    <div className="space-y-6 py-7">
      {isPending && (
        <div className="fixed inset-0 z-[9999999999999999999999999999999999] flex h-full w-full items-center justify-center bg-primary/35 backdrop-blur-sm">
          <Spinner />
        </div>
      )}

      <section className="overflow-hidden rounded-3xl border border-black/10 bg-white shadow">
        <div className="flex flex-col gap-5 border-b border-black/10 bg-primary px-6 py-6 text-white xl:flex-row xl:items-center xl:justify-between">
          <div>
            <p className="text-xs font-bold uppercase text-white/70">
              Student enrollment
            </p>
            <h1 className="mt-1 text-3xl font-bold">
              Assign Course to Student
            </h1>
          </div>
          <div className="grid grid-cols-3 gap-3 text-center xl:min-w-[420px]">
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="text-2xl font-bold">{courses.length}</p>
              <p className="text-xs font-semibold text-white/70">Courses</p>
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="text-2xl font-bold">{students.length}</p>
              <p className="text-xs font-semibold text-white/70">Results</p>
            </div>
            <div className="rounded-2xl bg-white/10 px-4 py-3">
              <p className="text-2xl font-bold">{activeFilters}</p>
              <p className="text-xs font-semibold text-white/70">Filters</p>
            </div>
          </div>
        </div>

        {isLoading ? (
          <div className="flex min-h-[50vh] items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div className="space-y-6 p-5 lg:p-6">
            <section className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
              <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 xl:grid-cols-[minmax(280px,1.4fr)_repeat(3,minmax(0,1fr))_160px_180px] xl:items-end">
                <div className="min-w-0">
                  <label className="mb-2 block text-sm font-bold text-textPrimary">
                    Course
                  </label>
                  <Listbox value={selected} onChange={setSelected}>
                    <div className="relative">
                      <Listbox.Button className="relative block h-12 w-full rounded-2xl border border-black/10 bg-dashboardBg px-4 py-3 text-left text-sm font-semibold text-textPrimary shadow-sm transition hover:border-secondary/50 focus:border-secondary focus:outline-none focus:ring-4 focus:ring-secondary/10">
                        <span className="block truncate">
                          {selected?.name || "Select a course"}
                        </span>
                        <span className="pointer-events-none absolute inset-y-0 right-4 flex items-center text-lightGray">
                          <ChevronDown size={18} />
                        </span>
                      </Listbox.Button>
                      <Transition
                        as={Fragment}
                        leave="transition ease-in duration-100"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                      >
                        <Listbox.Options className="absolute z-[9999] mt-2 max-h-72 w-full overflow-auto rounded-2xl border border-black/10 bg-white p-1 text-base shadow-[0_18px_55px_rgba(17,24,22,0.16)] focus:outline-none sm:text-sm">
                          {courses.map((option) => (
                            <Listbox.Option
                              key={option.id}
                              className={({ active }) =>
                                `relative cursor-default select-none rounded-xl py-3 pl-10 pr-4 text-textPrimary ${
                                  active ? "bg-secondary/10 text-primary" : ""
                                }`
                              }
                              value={option}
                            >
                              {({ selected }) => (
                                <>
                                  <span
                                    className={`block truncate text-sm ${
                                      selected ? "font-bold" : "font-medium"
                                    }`}
                                  >
                                    {option?.name}
                                  </span>
                                  {selected ? (
                                    <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-secondary">
                                      <Check size={16} />
                                    </span>
                                  ) : null}
                                </>
                              )}
                            </Listbox.Option>
                          ))}
                        </Listbox.Options>
                      </Transition>
                    </div>
                  </Listbox>
                </div>

                <Input
                  title="First name"
                  name="FirstName"
                  type="text"
                  className="md:!space-y-1"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  placeholder="Search name"
                  inputStyles="!h-12"
                />
                <Input
                  title="Phone"
                  name="Phone"
                  type="text"
                  className="md:!space-y-1"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="Search phone"
                  inputStyles="!h-12"
                />
                <Input
                  title="Email"
                  name="email"
                  type="email"
                  className="md:!space-y-1"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Search email"
                  inputStyles="!h-12"
                />

                <Button
                  className="!h-12 w-full !rounded-2xl !text-sm font-bold"
                  variant="primary"
                  type={ButtonTypes.BUTTON}
                  onClick={handleSearch}
                >
                  <Search size={17} />
                  Search
                </Button>
                <Button
                  className="!h-12 w-full !rounded-2xl !text-sm font-bold"
                  variant="primary"
                  type={ButtonTypes.BUTTON}
                  onClick={handleAssignCourse}
                >
                  <CheckCircle2 size={18} />
                  Assign
                </Button>
              </div>

              <div className="mt-5 grid gap-4 rounded-2xl border border-black/10 bg-dashboardBg p-4 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
                <div className="flex min-w-0 items-center gap-3">
                  <span className="grid size-12 shrink-0 place-items-center rounded-2xl bg-primary/10 text-primary">
                    {studentSelected ? (
                      <span className="text-sm font-bold">
                        {getInitials(studentSelected)}
                      </span>
                    ) : (
                      <UserRound size={20} />
                    )}
                  </span>
                  <div className="min-w-0">
                    <p className="truncate text-sm font-bold text-textPrimary">
                      {studentSelected
                        ? getStudentName(studentSelected)
                        : "No student selected"}
                    </p>
                    <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs font-semibold text-lightGray">
                      <span className="inline-flex items-center gap-1">
                        <BookOpenCheck size={14} />
                        {selected?.name || "No course selected"}
                      </span>
                      {studentSelected && (
                        <>
                          <span className="inline-flex items-center gap-1">
                            <Mail size={14} />
                            {studentSelected.email}
                          </span>
                          <span className="inline-flex items-center gap-1">
                            <Phone size={14} />
                            {studentSelected.phone || "No phone"}
                          </span>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <span className="inline-flex w-fit rounded-full bg-white px-4 py-2 text-xs font-bold text-primary">
                  Page {metadata?.currentPage || 1} of{" "}
                  {metadata?.totalPages || 1}
                </span>
              </div>
            </section>

            <section className="rounded-2xl border border-black/10 bg-white p-5 shadow-sm">
              <div className="mb-5 flex flex-col gap-3 border-b border-black/10 pb-5 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-3">
                  <span className="grid size-11 place-items-center rounded-2xl bg-primary text-white">
                    <UsersRound size={20} />
                  </span>
                  <div>
                    <h2 className="text-xl font-bold text-textPrimary">
                      Students
                    </h2>
                    <p className="text-sm font-medium text-lightGray">
                      {students.length} results on this page
                    </p>
                  </div>
                </div>
                <span className="rounded-full bg-secondary/10 px-4 py-2 text-sm font-bold text-primary">
                  {activeFilters ? `${activeFilters} active filters` : "All students"}
                </span>
              </div>

              <div className="grid grid-cols-1 gap-4 md:grid-cols-2 2xl:grid-cols-3">
                {students.length ? (
                  students.map((student) => {
                    const isSelected = studentSelected?.id === student.id;

                    return (
                      <button
                        key={student.id}
                        type="button"
                        onClick={() => setStudentSelected(student)}
                        className={`group min-w-0 rounded-2xl border p-4 text-left transition duration-200 ${
                          isSelected
                            ? "border-secondary bg-secondary/10 shadow-[0_18px_45px_rgba(254,87,34,0.16)]"
                            : "border-black/10 bg-white hover:-translate-y-0.5 hover:border-primary/25 hover:shadow-[0_18px_45px_rgba(12,48,82,0.1)]"
                        }`}
                      >
                        <div className="mb-4 flex items-start justify-between gap-3">
                          <div className="flex min-w-0 items-center gap-3">
                            <span
                              className={`grid size-12 shrink-0 place-items-center rounded-2xl text-sm font-bold ${
                                isSelected
                                  ? "bg-secondary text-white"
                                  : "bg-primary text-white"
                              }`}
                            >
                              {getInitials(student)}
                            </span>
                            <div className="min-w-0">
                              <p className="truncate font-bold text-textPrimary">
                                {getStudentName(student)}
                              </p>
                              <p className="truncate text-xs font-semibold text-lightGray">
                                {student.email}
                              </p>
                            </div>
                          </div>
                          <span
                            className={`grid size-7 shrink-0 place-items-center rounded-full border ${
                              isSelected
                                ? "border-secondary bg-secondary text-white"
                                : "border-black/15 bg-white text-transparent"
                            }`}
                          >
                            <Check size={15} />
                          </span>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-lightGray">
                          <span className="inline-flex min-w-0 items-center gap-2 truncate rounded-full bg-dashboardBg px-3 py-1">
                            <Phone size={15} />
                            <span className="truncate">
                              {student.phone || "No phone"}
                            </span>
                          </span>
                          <span
                            className={`inline-flex rounded-full px-3 py-1 text-xs font-bold capitalize ${
                              student.status === "active"
                                ? "bg-green-50 text-green-700"
                                : "bg-red-50 text-red-700"
                            }`}
                          >
                            {student.status}
                          </span>
                        </div>
                      </button>
                    );
                  })
                ) : (
                  <div className="col-span-full rounded-2xl border border-dashed border-black/15 bg-dashboardBg p-10 text-center">
                    <p className="text-lg font-bold text-textPrimary">
                      No students found
                    </p>
                    <p className="mt-1 text-sm font-medium text-lightGray">
                      Try another name, phone, or email.
                    </p>
                  </div>
                )}
              </div>

              <div className="mt-6 flex flex-col gap-3 border-t border-black/10 pt-5 sm:flex-row sm:items-center sm:justify-between">
                <Button
                  className="!h-11 w-full !rounded-2xl !text-sm font-bold sm:w-40"
                  variant="primary"
                  type={ButtonTypes.BUTTON}
                  onClick={handlePrevPage}
                  disabled={!metadata?.previousPage}
                >
                  <ChevronLeft size={17} />
                  Previous
                </Button>
                <span className="text-center text-sm font-bold text-lightGray">
                  Page {metadata?.currentPage || 1} of{" "}
                  {metadata?.totalPages || 1}
                </span>
                <Button
                  className="!h-11 w-full !rounded-2xl !text-sm font-bold sm:w-40"
                  variant="primary"
                  type={ButtonTypes.BUTTON}
                  onClick={handleNextPage}
                  disabled={!metadata?.nextPage}
                >
                  Next
                  <ChevronRight size={17} />
                </Button>
              </div>
            </section>
          </div>
        )}
      </section>
    </div>
  );
};

export default AssignCourse;
